import { useRef } from 'react';
import type { LinhaLista } from '../types';

interface CampoListaProps {
  pergunta: { id: number; opcoes: string[] };
  valor: LinhaLista[] | undefined;
  onChange: (valor: LinhaLista[]) => void;
}

// Abre o teclado numérico no celular para colunas que são número.
// Sem formatar, sem validar — dado torto é melhor que dado impossível de digitar.
function colunaNumerica(coluna: string): boolean {
  return /idade|ano|quantidade|telefone|cpf/i.test(coluna);
}

// Uma linha nova começa sem nenhuma coluna preenchida.
function novaLinha(): LinhaLista {
  return {};
}

export default function CampoLista({ pergunta, valor, onChange }: CampoListaProps) {
  const colunas = pergunta.opcoes.filter((c) => c.trim() !== '');
  const linhas = valor && valor.length > 0 ? valor : [novaLinha()];

  const primeiroCampoRefs = useRef<Record<number, HTMLInputElement | null>>({});

  function mudarLinha(indice: number, coluna: string, texto: string) {
    const novo = linhas.map((linha, i) =>
      i === indice ? { ...linha, [coluna]: texto } : linha,
    );
    onChange(novo);
  }

  function adicionar() {
    const novoIndice = linhas.length;
    onChange([...linhas, novaLinha()]);
    // A linha nova já nasce com o foco no primeiro campo dela.
    requestAnimationFrame(() => {
      primeiroCampoRefs.current[novoIndice]?.focus();
    });
  }

  function remover(indice: number) {
    if (linhas.length === 1) return; // ninguém apaga a última linha
    onChange(linhas.filter((_, i) => i !== indice));
  }

  return (
    <div>
      <p className="mb-3 text-sm text-secondary-500">
        Preencha um por vez e clique em &quot;Adicionar mais um&quot; para o próximo.
      </p>

      <div className="space-y-3">
        {linhas.map((linha, indice) => (
          <div key={indice} className="rounded-lg border border-secondary-200 p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm font-medium text-secondary-400">{indice + 1}</span>
              {linhas.length > 1 && (
                <button
                  type="button"
                  onClick={() => remover(indice)}
                  className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full text-secondary-400 transition-colors hover:bg-background-100 hover:text-danger-600"
                  aria-label={`Remover linha ${indice + 1}`}
                >
                  <i className="ri-close-line" />
                </button>
              )}
            </div>

            <div className="space-y-3">
              {colunas.map((coluna, c) => {
                const numerica = colunaNumerica(coluna);
                return (
                  <div key={coluna}>
                    <label className="mb-1 block text-xs font-medium text-foreground-800">
                      {coluna}
                    </label>
                    <input
                      ref={(el) => {
                        if (c === 0) primeiroCampoRefs.current[indice] = el;
                      }}
                      type="text"
                      inputMode={numerica ? 'numeric' : undefined}
                      value={linha[coluna] ?? ''}
                      onChange={(e) => mudarLinha(indice, coluna, e.target.value)}
                      className="w-full rounded-md border border-secondary-300 px-3 py-2 text-[15px] text-foreground-950 outline-none transition-colors focus:border-primary-500"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={adicionar}
        className="mt-3 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-500 px-4 py-2.5 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50"
      >
        <span className="flex h-5 w-5 items-center justify-center">
          <i className="ri-add-line" />
        </span>
        Adicionar mais um
      </button>
    </div>
  );
}